import _get from 'lodash/get';
import _isEqual from 'lodash/isEqual';
import {
  FilterGroupingEnum,
  User,
  UserFilterInputObject,
  UserRoleEnum,
  UsersQueryVariables,
  useUsersCountersQuery,
  useUsersIdsLazyQuery,
  useUsersIdsQuery,
  useUsersQuery,
} from 'graphql-common';
import { GetUrlParams } from '@lib/enums/urls';
import createSearchFilters from '@lib/utils/createSearchFilters';
import usePrevious from '@lib/hooks/usePrevious';
import { useCallback, useEffect, useRef } from 'react';
import { ListQueryHookProps, ListQueryHookResult } from '../types';

type Args = {
  includeFinalAccesses?: boolean;
  includeFullData?: boolean;
  includePersonalAccesses?: boolean;
  includeShortData?: boolean;
  includeTeamAccesses?: boolean;
  includeTeamData?: boolean;
  skipGetCounters?: boolean;
};

export type UsersCounters = {
  all: number;
  fullAdmins: number;
  unassigned: number;
  users: number;
};

export default function useUsersQueryHook({
  additionalFilters,
  includeFinalAccesses = false,
  includeFullData = false,
  includePersonalAccesses = false,
  includeShortData = false,
  includeTeamAccesses = false,
  includeTeamData = false,
  paginationParams,
  searchQuery,
  skip,
  skipGetAllIds = true,
  skipGetCounters = true,
}: ListQueryHookProps & Args): ListQueryHookResult<User> & {
  counters: UsersCounters;
} {
  const prevAdditionalFilters = usePrevious(additionalFilters);
  const page = _get(paginationParams, GetUrlParams.Page);
  const perPage = _get(paginationParams, GetUrlParams.PerPage);
  const orderingField = _get(paginationParams, GetUrlParams.OrderingField);
  const orderingDirection = _get(
    paginationParams,
    GetUrlParams.OrderingDirection,
  );
  const usersQueryVariables: UsersQueryVariables = {
    page: Number(page),
    limit: Number(perPage),
    includeFinalAccesses,
    includeFullData,
    includePersonalAccesses,
    includeTeamAccesses,
    includeTeamData,
    includeShortData,
  };
  const filters: UserFilterInputObject[] = [];
  const searchFilters = searchQuery
    ? createSearchFilters(searchQuery, [
        { fieldName: 'email' },
        { fieldName: 'username' },
        { fieldName: 'teamName' },
        { fieldName: 'fullName' },
        { fieldName: 'personalId' },
        { fieldName: 'memberAssignedSitesName' },
      ])
    : [];
  if (searchQuery) {
    filters.push(...searchFilters);
  }
  if (additionalFilters?.length) {
    filters.push(...additionalFilters);
  }
  if (filters.length) {
    usersQueryVariables.filters = filters;
  }
  if (orderingField && orderingDirection) {
    usersQueryVariables.sorts = {
      [orderingField]: orderingDirection,
    };
  }
  const usersQuery = useUsersQuery({
    fetchPolicy: 'cache-and-network',
    variables: usersQueryVariables,
    skip,
  });
  const allIdsQuery = useUsersIdsQuery({
    fetchPolicy: 'no-cache',
    variables: {
      onlyIds: true,
      filters: usersQueryVariables.filters,
    },
    skip: skipGetAllIds,
  });
  const shouldResetCounters =
    additionalFilters && !_isEqual(additionalFilters, prevAdditionalFilters);
  const countersQuery = useUsersCountersQuery({
    fetchPolicy: 'cache-and-network',
    variables: {
      onlyIds: false,
      filters: searchFilters,
      fullAdminsFilters: [
        ...searchFilters,
        {
          role: {
            grouping: FilterGroupingEnum.Or,
            predicate: { eq: UserRoleEnum.FullAdmin },
          },
        },
      ],
      usersFilters: [
        ...searchFilters,
        {
          role: {
            grouping: FilterGroupingEnum.Or,
            predicate: { eq: UserRoleEnum.User },
          },
        },
      ],
      unassignedFilters: [
        ...searchFilters,
        {
          role: {
            grouping: FilterGroupingEnum.And,
            predicate: { eq: UserRoleEnum.User },
          },
        },
        {
          siteMembershipsId: {
            grouping: FilterGroupingEnum.And,
            predicate: { null: true },
          },
        },
        {
          sectorMembershipsId: {
            grouping: FilterGroupingEnum.And,
            predicate: { null: true },
          },
        },
      ],
    },
    skip: skipGetCounters,
  });
  const [fetchIdsQuery] = useUsersIdsLazyQuery({
    fetchPolicy: 'no-cache',
  });
  const fetchIdsVariables = {
    onlyIds: true,
    filters: usersQueryVariables.filters,
  };
  const fetchIdsVariablesRef = useRef(fetchIdsVariables);
  fetchIdsVariablesRef.current = fetchIdsVariables;
  // Stable ref so fetchIds is safe as a useEffect dependency (no render loop).
  const fetchIds = useCallback(
    () => fetchIdsQuery({ variables: fetchIdsVariablesRef.current }),
    [fetchIdsQuery],
  );
  const { loading, data, previousData, refetch, error } = usersQuery;
  const queryData = data || previousData;
  const firstLoading = loading && previousData === undefined;
  const collection = _get(queryData, 'data.collection', []) as User[];
  const metadata = _get(queryData, 'data.metadata');
  const totalCount = _get(metadata, 'totalCount', 0);
  const totalPages = _get(metadata, 'totalPages', 1);
  const counters: UsersCounters = {
    all: _get(countersQuery, 'data.all.metadata.totalCount', 0),
    fullAdmins: _get(countersQuery, 'data.fullAdmins.metadata.totalCount', 0),
    unassigned: _get(countersQuery, 'data.unassigned.metadata.totalCount', 0),
    users: _get(countersQuery, 'data.users.metadata.totalCount', 0),
  };
  useEffect(() => {
    if (shouldResetCounters && !skipGetCounters) {
      countersQuery.refetch();
    }
  }, [countersQuery, shouldResetCounters, skipGetCounters]);
  return {
    collection,
    error,
    fetchIds,
    firstLoading,
    loading,
    refetch,
    totalCount,
    totalPages,
    allIds: _get(allIdsQuery, ['data', 'data', 'collection'], []),
    refetchAllIds: allIdsQuery.refetch,
    counters,
  };
}
